import React, { useCallback, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import {
  Botao,
  BotaoSelecao,
  Field,
  FormActions,
  FormSection,
  Input,
  RadioCards,
  Select,
} from '../../ui';
import Icone from '../../ui/primitivos/Icone';

import ModalRegionaisUsuario from '../modal/regionais-usuario/ModalRegionaisUsuario';
import ModalImportacaoSga from '../modal/importacao-sga/ModalImportacaoSga';

import { fetchAssociadoPorCpfCnpj, Associado } from '../../api/associado/associadoApi';
import {
  importarAssociadoSga,
  previewAssociadoSga,
  PreviewAssociadoSga,
} from '../../api/sga-importacao/sgaImportacaoApi';

import { formatCpfCnpj } from '../../utils/formartCPF';
import {
  RG_MAX,
  UFS,
  formatCep,
  formatRg,
  formatTelefone,
  juntarDddNumero,
  separarDddNumero,
} from '../../utils/mascaras';
import { buscarEnderecoPorCep } from '../../utils/cep';
import {
  validarCep,
  validarCpfCnpj,
  validarDataNascimento,
  validarEmail,
  validarObrigatorio,
  validarTelefone,
  mensagemCpfJaCadastrado,
} from '../../utils/validacaoAssociado';
import { extrairMensagemErro } from '../../utils/apiError';

/**
 * O formulário do titular — **um componente para as três telas** (at-58, frente C).
 *
 * ## Por que existe
 *
 * Medido em 09/09/2026: `associado.tsx` (512 linhas), `associadoEdit.tsx` (583) e
 * `associadoSubstituir.tsx` (708) declaravam **o mesmo formulário de 19 campos** —
 * os mesmos 15 `<input>`, os mesmos 2 `<select>`, as mesmas 6 funções de máscara
 * copiadas e a mesma lista de UFs. As diferenças reais eram **quatro**: para onde
 * o "Salvar" navega, se grava rascunho, o título do primeiro bloco, e os blocos
 * de busca/importação. 1.803 linhas viram este arquivo e três telas finas.
 *
 * E não é só volume: enquanto eram três cópias, **toda correção tinha de ser
 * feita três vezes** — foi assim que a máscara de RG, o parse de CEP e a lista de
 * UFs se multiplicaram por seis (as 3 de carro incluídas).
 *
 * ## 🔴 Contratos que este componente NÃO pode mudar (spec 03 §9)
 *
 * As chaves que ele devolve em {@link DadosAssociado} são lidas **por nome** pelas
 * telas seguintes e pelo montador do payload de `POST /associado/cadastrar`
 * (`views/documento`), e são o formato dos **rascunhos já gravados no banco** —
 * que o front novo tem de conseguir retomar. Renomear qualquer uma quebra o
 * cadastro em silêncio.
 *
 * ⚠️ `dddTelefone` (e não `dddTelefoneFixo`) é o nome no estado do front; a coluna
 * do banco é `dddTelefoneFixo`. A ponte é feita **no envio**, em
 * `views/documento/pages/documento.tsx` — deixar como está evita mexer no shape
 * do slice e nos rascunhos gravados.
 *
 * ## Camada
 *
 * Mora em `components/` e não em `src/ui` porque conhece o domínio: chama a API
 * de associado, a de importação do SGA e o modal de regionais do consultor. O kit
 * que ele usa (`Field`, `Input`, `Select`, `RadioCards`, `FormSection`,
 * `FormActions`) é puro — precedente do `ShellSaac` (`13-ui-kit.md`).
 */

/** O que o formulário devolve — as chaves do estado do fluxo (spec 03 §9.1). */
export interface DadosAssociado {
  cpfCnpj: string;
  nomeCompleto: string;
  rg: string;
  dataNascimento: string;
  sexo: string;
  email: string;
  dddCelular: string;
  celular: string;
  dddTelefone: string;
  telefoneFixo: string;
  regionalId: string;
  regionalNome: string;
  cep: string;
  rua: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  estado: string;
}

/**
 * Estado interno da tela. Difere de {@link DadosAssociado} em **um** ponto: os
 * telefones vivem unificados (`celularUnificado`, `telefoneFixoUnificado`), com
 * DDD e número no mesmo campo — autorizado pela spec 03 §3.3 e é como o protótipo
 * aprovado faz. A separação de volta acontece no submit.
 */
interface EstadoForm {
  cpfCnpj: string;
  nomeCompleto: string;
  rg: string;
  dataNascimento: string;
  sexo: string;
  email: string;
  celularUnificado: string;
  telefoneFixoUnificado: string;
  regionalId: string;
  regionalNome: string;
  cep: string;
  rua: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  estado: string;
}

type CampoForm = keyof EstadoForm;
type Erros = Partial<Record<CampoForm, string>>;

const VAZIO: EstadoForm = {
  cpfCnpj: '',
  nomeCompleto: '',
  rg: '',
  dataNascimento: '',
  sexo: '',
  email: '',
  celularUnificado: '',
  telefoneFixoUnificado: '',
  regionalId: '',
  regionalNome: '',
  cep: '',
  rua: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  estado: '',
};

/** D8: o backend só aceita estes dois — "Outro" era recusado com 400 na etapa 3. */
const OPCOES_SEXO = [
  { valor: 'MASCULINO', rotulo: 'Masculino' },
  { valor: 'FEMININO', rotulo: 'Feminino' },
] as const;

/** Estado da checagem antecipada de CPF (D3). */
type EstadoCpf = 'parado' | 'checando' | 'livre' | 'existe';

export interface PropsFormularioAssociado {
  /** Título do primeiro bloco — "Identificação do Novo Associado" na troca de titular. */
  tituloIdentificacao?: string;
  /** Rótulo da ação principal (ex.: "Continuar — Veículo"). */
  rotuloAcao: string;
  /** Preenchimento inicial. Ausente = formulário vazio. */
  valoresIniciais?: Partial<DadosAssociado> | null;
  /**
   * Botão "Buscar Associado" no bloco de identificação: carrega um cadastro que
   * já existe no SAAC. É o que as duas telas de substituição usam para não
   * redigitar o titular.
   */
  comBuscaLocal?: boolean;
  /**
   * Chamado quando a busca local encontra o associado. Existe porque a troca de
   * titular precisa guardar o `codigoAssociadoNovo` no slice do carro — efeito
   * que é só dela.
   */
  aoEncontrarLocal?: (associado: Associado) => void;
  /**
   * Oferece "Buscar no SGA" quando a busca local dá 404 (at-32, item 30): o
   * titular pode já existir no SGA e nunca ter passado pelo SAAC.
   */
  comImportacaoSga?: boolean;
  /**
   * **D3** — ao sair do campo de CPF, avisa se ele já tem cadastro. Só faz
   * sentido na adesão NOVA: nas substituições um CPF que já existe é justamente
   * o que se procura.
   */
  comChecagemCpf?: boolean;
  /** Aviso opcional acima do formulário (a faixa de rascunhos em andamento). */
  aviso?: React.ReactNode;
  aoSalvar: (dados: DadosAssociado) => void;
}

/** Data ISO a partir do que a API devolve (`Date`, ISO com hora, ou já ISO). */
function normalizarDataApi(valor: unknown): string {
  if (!valor) return '';
  const texto = String(valor);
  if (texto.includes(' ')) return texto.split(' ')[0];
  try {
    return new Date(texto).toISOString().slice(0, 10);
  } catch {
    return texto;
  }
}

const FormularioAssociado: React.FC<PropsFormularioAssociado> = ({
  tituloIdentificacao = 'Identificação',
  rotuloAcao,
  valoresIniciais,
  comBuscaLocal = false,
  aoEncontrarLocal,
  comImportacaoSga = false,
  comChecagemCpf = false,
  aviso,
  aoSalvar,
}) => {
  /*
   * ⚠️ O estado nasce dos `valoresIniciais` **uma vez** (inicializador do
   * `useState`), e não por `useEffect` que sincroniza.
   *
   * 🔴 Isto atende a spec 03 §7.2 — "a tela deve abrir preenchida quando há dados
   * no fluxo" — sem criar o defeito oposto: um efeito que reescrevesse o estado a
   * cada mudança da prop apagaria o que o consultor está digitando (o slice muda
   * quando a busca local ou a importação gravam). Trocar de titular no meio do
   * preenchimento não é caso de uso; digitar, é.
   */
  const [form, setForm] = useState<EstadoForm>(() => {
    if (!valoresIniciais) return VAZIO;

    return {
      ...VAZIO,
      ...paraEstado(valoresIniciais),
    };
  });

  const [erros, setErros] = useState<Erros>({});
  const [salvando, setSalvando] = useState(false);
  const [modalRegionaisAberto, setModalRegionaisAberto] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  // D3 — checagem antecipada de CPF.
  const [estadoCpf, setEstadoCpf] = useState<EstadoCpf>('parado');
  const [avisoCpfExistente, setAvisoCpfExistente] = useState('');
  /** Dígitos da última consulta disparada — resposta de CPF antigo é descartada. */
  const cpfConsultadoRef = useRef('');
  /** O texto do aviso, legível logo após o `await` (o estado ainda não renderizou). */
  const avisoCpfRef = useRef('');

  // D4 — consulta de CEP.
  const [buscandoCep, setBuscandoCep] = useState(false);

  // Busca local (telas de substituição).
  const [buscandoLocal, setBuscandoLocal] = useState(false);

  // Importação do SGA (at-32).
  const [cpfSemRegistroLocal, setCpfSemRegistroLocal] = useState<string | null>(null);
  const [previewSga, setPreviewSga] = useState<PreviewAssociadoSga | null>(null);
  const [modalImportacaoAberto, setModalImportacaoAberto] = useState(false);
  const [buscandoNoSga, setBuscandoNoSga] = useState(false);
  const [importandoDoSga, setImportandoDoSga] = useState(false);

  /** Escreve um campo e **limpa o erro dele** — a mensagem não fica de teimosa. */
  const definir = useCallback((campo: CampoForm, valor: string) => {
    setForm((antes) => ({ ...antes, [campo]: valor }));
    setErros((antes) => (antes[campo] ? { ...antes, [campo]: undefined } : antes));
  }, []);

  /** Aplica no formulário um cadastro vindo da API (busca local / importação). */
  const preencherComAssociado = useCallback((r: Associado) => {
    setForm((antes) => ({
      ...antes,
      nomeCompleto: r.nomeCompleto ?? antes.nomeCompleto,
      rg: r.rg ?? antes.rg,
      dataNascimento: normalizarDataApi(r.dataNascimento),
      // O backend guarda MAIÚSCULO; o `RadioCards` compara valor a valor.
      sexo: r.sexo ? String(r.sexo).toUpperCase() : '',
      email: r.email ?? antes.email,
      celularUnificado: juntarDddNumero(r.dddCelular, r.celular),
      telefoneFixoUnificado: juntarDddNumero(
        (r as any).dddTelefoneFixo ?? (r as any).dddTelefone,
        (r as any).telefoneFixo,
      ),
      cep: formatCep(r.cep ?? ''),
      rua: r.rua ?? antes.rua,
      numero: r.numero ?? antes.numero,
      complemento: r.complemento ?? antes.complemento,
      bairro: r.bairro ?? antes.bairro,
      cidade: r.cidade ?? antes.cidade,
      estado: r.estado ?? antes.estado,
      regionalId: (r as any).regional ?? antes.regionalId,
      regionalNome:
        (r as any).regionalNome ?? (r as any).regionalName ?? antes.regionalNome,
    }));
    // O que veio do cadastro é válido por definição — segurar erro de um campo
    // que acabou de ser preenchido só confundiria.
    setErros({});
  }, []);

  // ── D3: CPF já cadastrado ─────────────────────────────────────────────────

  /**
   * Consulta o CPF assim que ele fica completo e válido (at-83) — não espera o
   * blur nem o "Salvar".
   *
   * 🔴 **CPF já cadastrado BLOQUEIA o avanço** (decisão de Victor, 03/11/2026 —
   * substitui o "avisa sem bloquear" da D3). O cadastro seria recusado com
   * certeza na etapa 3, depois das fotos: caso real do consultor Michel, que
   * tentou um 2º veículo para uma associada da carteira de outro consultor.
   * O texto diz o titular, o dono da carteira e manda falar com o TI — a
   * Substituição Veicular TROCA o veículo, não acrescenta outro.
   *
   * ⚠️ **Falha da consulta continua não travando nada** (spec 03 §6): sem resposta
   * o campo segue válido e o cadastro tenta normalmente. Só bloqueia o que o
   * servidor confirmou que existe.
   */
  const checarCpf = async (valor: string = form.cpfCnpj): Promise<EstadoCpf> => {
    if (!comChecagemCpf) return 'parado';

    const digitos = valor.replace(/\D/g, '');
    if (validarCpfCnpj(digitos) !== null) return 'parado';

    cpfConsultadoRef.current = digitos;
    setEstadoCpf('checando');

    let resultado: EstadoCpf;
    let aviso = '';
    try {
      const achado = await fetchAssociadoPorCpfCnpj(digitos);
      aviso = mensagemCpfJaCadastrado(achado?.nomeCompleto, achado?.consultorNome);
      resultado = 'existe';
    } catch (erro) {
      const status = (erro as { response?: { status?: number } })?.response?.status;
      // 404 é a boa notícia: o CPF está livre.
      resultado = status === 404 ? 'livre' : 'parado';
    }

    // O consultor mudou o CPF enquanto a consulta corria: esta resposta é velha.
    if (cpfConsultadoRef.current !== digitos) return 'parado';

    setEstadoCpf(resultado);
    setAvisoCpfExistente(aviso);
    avisoCpfRef.current = aviso;
    if (resultado === 'existe') setErros((antes) => ({ ...antes, cpfCnpj: aviso }));
    return resultado;
  };

  // ── D4: CEP ───────────────────────────────────────────────────────────────

  /**
   * Preenche rua/bairro/cidade/UF a partir do CEP.
   *
   * ⚠️ **Nunca sobrescreve com vazio.** Alguns CEPs de cidade inteira não têm
   * logradouro; aplicar `''` apagaria a rua que o consultor já digitou.
   * ⚠️ **Falha é silenciosa** e os campos seguem editáveis (spec 03 §3.5) — a
   * consulta é conveniência, não pré-requisito.
   */
  const consultarCep = async () => {
    const digitos = form.cep.replace(/\D/g, '');
    if (digitos.length !== 8) return;

    setBuscandoCep(true);
    try {
      const endereco = await buscarEnderecoPorCep(digitos);
      if (!endereco) return;

      setForm((antes) => ({
        ...antes,
        rua: endereco.rua || antes.rua,
        bairro: endereco.bairro || antes.bairro,
        cidade: endereco.cidade || antes.cidade,
        estado: endereco.estado || antes.estado,
      }));
      setErros((antes) => ({
        ...antes,
        rua: undefined,
        bairro: undefined,
        cidade: undefined,
        estado: undefined,
      }));
    } finally {
      setBuscandoCep(false);
    }
  };

  // ── Busca local e importação do SGA ───────────────────────────────────────

  const buscarLocal = async () => {
    const digitos = form.cpfCnpj.replace(/\D/g, '');
    if (!digitos) return;

    setBuscandoLocal(true);
    setCpfSemRegistroLocal(null);
    try {
      const achado = await fetchAssociadoPorCpfCnpj(digitos);
      if (achado) {
        preencherComAssociado(achado);
        aoEncontrarLocal?.(achado);
      }
    } catch (erro) {
      const status = (erro as { response?: { status?: number } })?.response?.status;

      // 404: ou é gente nova, ou já é associado no SGA e nunca passou pelo SAAC —
      // por isso oferecemos a importação em vez de só mandar digitar (at-32).
      if (status === 404) {
        if (comImportacaoSga) {
          setCpfSemRegistroLocal(digitos);
          toast.info(
            'Associado não encontrado no SAAC. Se ele já existe no SGA, use "Buscar no SGA"; senão, preencha os dados do novo titular.',
          );
        } else {
          toast.info('Associado não encontrado no SAAC. Preencha os dados abaixo.');
        }
      } else {
        toast.error(extrairMensagemErro(erro, 'Erro ao buscar o associado.'));
      }
    } finally {
      setBuscandoLocal(false);
    }
  };

  const buscarNoSga = async () => {
    const digitos = form.cpfCnpj.replace(/\D/g, '');

    if (digitos.length !== 11 && digitos.length !== 14) {
      toast.warning('Informe um CPF/CNPJ completo para buscar no SGA.');
      return;
    }

    setBuscandoNoSga(true);
    setPreviewSga(null);
    setModalImportacaoAberto(true);

    try {
      setPreviewSga(await previewAssociadoSga(digitos));
    } catch (erro) {
      const status = (erro as { status?: number | null })?.status ?? null;
      setModalImportacaoAberto(false);

      if (status === 404) {
        toast.info('Nenhum associado com este CPF/CNPJ foi encontrado no SGA.');
        return;
      }

      toast.error(extrairMensagemErro(erro, 'Não foi possível consultar o associado no SGA.'));
    } finally {
      setBuscandoNoSga(false);
    }
  };

  /** Importa e recarrega pelo caminho normal, para o fluxo seguir igual. */
  const confirmarImportacao = async () => {
    if (!previewSga) return;

    setImportandoDoSga(true);
    try {
      const resultado = await importarAssociadoSga(previewSga.cpfCnpj);
      toast.success(resultado.mensagem);

      setModalImportacaoAberto(false);
      setPreviewSga(null);
      setCpfSemRegistroLocal(null);

      await buscarLocal();
    } catch (erro) {
      toast.error(extrairMensagemErro(erro, 'Não foi possível importar o associado do SGA.'));
    } finally {
      setImportandoDoSga(false);
    }
  };

  // ── Validação e submit ────────────────────────────────────────────────────

  /**
   * Valida tudo de uma vez, no submit.
   *
   * ⚠️ Antes da at-58 a única regra era "não vazio" (mais o `type="email"` do
   * navegador): CPF com dígito verificador errado, e-mail malformado e data
   * impossível **passavam** e só eram recusados na etapa 3, depois de o consultor
   * preencher o veículo e fotografar os documentos (spec 03 §6).
   */
  const validar = (): Erros => {
    const e: Erros = {};

    e.cpfCnpj = validarCpfCnpj(form.cpfCnpj) ?? undefined;
    e.nomeCompleto = validarObrigatorio(form.nomeCompleto) ?? undefined;
    e.rg = validarObrigatorio(form.rg) ?? undefined;
    e.dataNascimento = validarDataNascimento(form.dataNascimento) ?? undefined;
    e.sexo = form.sexo ? undefined : 'Selecione uma opção';
    e.email = validarEmail(form.email) ?? undefined;
    e.celularUnificado = validarTelefone(form.celularUnificado, true) ?? undefined;
    e.telefoneFixoUnificado = validarTelefone(form.telefoneFixoUnificado, false) ?? undefined;
    e.regionalId = form.regionalId
      ? undefined
      : 'Por favor, selecione uma regional antes de prosseguir.';
    e.cep = validarCep(form.cep) ?? undefined;
    e.rua = validarObrigatorio(form.rua) ?? undefined;
    e.numero = validarObrigatorio(form.numero) ?? undefined;
    e.bairro = validarObrigatorio(form.bairro) ?? undefined;
    e.cidade = validarObrigatorio(form.cidade) ?? undefined;
    e.estado = form.estado ? undefined : 'Selecione a UF';

    // at-83: CPF que o servidor confirmou existir não passa — o cadastro seria recusado.
    if (!e.cpfCnpj && comChecagemCpf && estadoCpf === 'existe') {
      e.cpfCnpj = avisoCpfExistente || mensagemCpfJaCadastrado(null, null);
    }

    // `undefined` explícito acima mantém a checagem exaustiva de campos legível;
    // aqui as chaves sem erro são descartadas para `Object.keys` valer.
    (Object.keys(e) as CampoForm[]).forEach((k) => {
      if (!e[k]) delete e[k];
    });

    return e;
  };

  const enviar = async (evento: React.FormEvent) => {
    evento.preventDefault();

    let encontrados = validar();

    /*
     * at-83: CPF que ainda não foi consultado (veio preenchido do fluxo, ou a
     * consulta anterior falhou) é conferido agora, antes de avançar. Falha de
     * rede devolve 'parado' e o cadastro segue — só bloqueia o que existe.
     */
    if (
      Object.keys(encontrados).length === 0 &&
      comChecagemCpf &&
      estadoCpf !== 'livre' &&
      estadoCpf !== 'existe'
    ) {
      setSalvando(true);
      const resultado = await checarCpf();
      setSalvando(false);
      if (resultado === 'existe') encontrados = { cpfCnpj: avisoCpfRef.current };
    }

    if (Object.keys(encontrados).length > 0) {
      setErros(encontrados);

      /*
       * Rola até o primeiro erro. Sem isto, num formulário de 5 blocos, a
       * mensagem pode nascer fora da área visível — e o consultor volta ao estado
       * que a spec 03 §4 chama de pior possível: não avança e não sabe por quê.
       */
      const primeiro = Object.keys(encontrados)[0];
      const alvo = formRef.current?.querySelector(`[data-field="${primeiro}"]`);

      /*
       * ⚠️ Chamada DEFENSIVA. `scrollIntoView` não existe no jsdom (o teste
       * pegou isso) nem é garantido em toda WebView antiga — e uma exceção aqui
       * abortaria o resto do `submit`, deixando o formulário sem reação visível
       * ao clique. Rolar é conveniência; mostrar o erro é o requisito.
       */
      if (typeof alvo?.scrollIntoView === 'function') {
        alvo.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      return;
    }

    setSalvando(true);

    const celular = separarDddNumero(form.celularUnificado);
    const fixo = separarDddNumero(form.telefoneFixoUnificado);

    aoSalvar({
      cpfCnpj: form.cpfCnpj,
      nomeCompleto: form.nomeCompleto.trim(),
      rg: form.rg.trim(),
      dataNascimento: form.dataNascimento,
      sexo: form.sexo,
      email: form.email.trim(),
      dddCelular: celular.ddd,
      celular: celular.numero,
      dddTelefone: fixo.ddd,
      telefoneFixo: fixo.numero,
      regionalId: form.regionalId,
      regionalNome: form.regionalNome,
      cep: form.cep,
      rua: form.rua.trim(),
      numero: form.numero.trim(),
      complemento: form.complemento.trim(),
      bairro: form.bairro.trim(),
      cidade: form.cidade.trim(),
      estado: form.estado,
    });

    /*
     * ⚠️ O `salvando` NÃO é desligado aqui: quem chama navega para a etapa
     * seguinte, e voltar o botão a "Continuar" antes disso pisca a tela e convida
     * a um segundo clique — que criaria um segundo rascunho.
     */
  };

  return (
    <form ref={formRef} onSubmit={enviar} noValidate className="tw-flex tw-flex-col tw-gap-8">
      {aviso}

      {/* ── 1. Identificação ────────────────────────────────────────────── */}
      <FormSection titulo={tituloIdentificacao} icone="identificacao">
        <div className="tw-flex tw-flex-col tw-gap-4">
          <Field rotulo="CPF / CNPJ" obrigatorio erro={erros.cpfCnpj} campo="cpfCnpj">
            <div className="tw-relative">
              <Input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                placeholder="000.000.000-00"
                maxLength={18}
                value={form.cpfCnpj}
                onChange={(evento) => {
                  const novo = formatCpfCnpj(evento.target.value);
                  definir('cpfCnpj', novo);
                  // Resposta de consulta em curso passa a ser de um CPF antigo.
                  cpfConsultadoRef.current = '';
                  setEstadoCpf('parado');
                  setAvisoCpfExistente('');
                  // at-83: consulta assim que o documento fica completo, sem esperar o blur.
                  const n = novo.replace(/\D/g, '').length;
                  if (n === 11 || n === 14) void checarCpf(novo);
                }}
                onBlur={() => {
                  if (estadoCpf === 'parado') void checarCpf();
                }}
              />

              {estadoCpf === 'checando' && (
                <span className="tw-absolute tw-right-3 tw-top-1/2 -tw-translate-y-1/2">
                  <span
                    className="tw-block tw-h-4 tw-w-4 tw-animate-spin tw-rounded-full tw-border-2 tw-border-superficie-borda tw-border-t-brand"
                    aria-hidden="true"
                  />
                </span>
              )}

              {estadoCpf === 'livre' && (
                <span
                  className="tw-absolute tw-right-3 tw-top-1/2 -tw-translate-y-1/2 tw-text-success"
                  aria-hidden="true"
                >
                  <Icone nome="check" tamanho={16} traco={2.5} />
                </span>
              )}
            </div>
          </Field>

          {comBuscaLocal && (
            <div className="tw-flex tw-flex-col tw-gap-2 sm:tw-flex-row sm:tw-items-center">
              <Botao
                variante="secundario"
                onClick={() => void buscarLocal()}
                disabled={!form.cpfCnpj.trim()}
                carregando={buscandoLocal}
                iconeEsquerda="busca"
              >
                Buscar Associado
              </Botao>
              <span className="tw-text-xs tw-text-texto-suave">
                Traz os dados de quem já é cadastrado, para não digitar de novo.
              </span>
            </div>
          )}

          {/* at-32: o titular pode já ser associado no SGA e nunca ter passado
              pelo SAAC — importar evita redigitar e mantém o vínculo correto. */}
          {comImportacaoSga && cpfSemRegistroLocal && (
            <div className="tw-flex tw-flex-col tw-gap-3 tw-rounded-xl tw-border tw-border-amber-warn/30 tw-bg-amber-bg tw-px-4 tw-py-3">
              <p className="tw-m-0 tw-text-xs tw-text-amber-deep">
                O CPF/CNPJ <strong>{cpfSemRegistroLocal}</strong> não está no SAAC. Se essa
                pessoa já é associada no SGA, traga o cadastro de lá em vez de digitar tudo
                de novo.
              </p>
              <Botao
                variante="secundario"
                tamanho="sm"
                onClick={() => void buscarNoSga()}
                carregando={buscandoNoSga}
                className="sm:tw-self-start"
              >
                Buscar no SGA
              </Botao>
            </div>
          )}
        </div>
      </FormSection>

      {/* ── 2. Dados do Associado ───────────────────────────────────────── */}
      <FormSection titulo="Dados do Associado" icone="pessoa">
        <div className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2">
          <Field
            rotulo="Nome completo"
            obrigatorio
            erro={erros.nomeCompleto}
            campo="nomeCompleto"
            className="sm:tw-col-span-2"
          >
            <Input
              type="text"
              autoComplete="name"
              placeholder="Como está no documento"
              maxLength={100}
              value={form.nomeCompleto}
              onChange={(evento) => definir('nomeCompleto', evento.target.value)}
            />
          </Field>

          {/* D5: alfanumérico livre. A máscara antiga assumia 9 dígitos no padrão
              SP e comia a letra do órgão emissor dos outros estados. */}
          <Field
            rotulo="RG"
            obrigatorio
            erro={erros.rg}
            campo="rg"
            dica="Aceita letras, como MG-1.234.567"
          >
            <Input
              type="text"
              autoComplete="off"
              placeholder="00.000.000-0"
              maxLength={RG_MAX}
              value={form.rg}
              onChange={(evento) => definir('rg', formatRg(evento.target.value))}
            />
          </Field>

          <Field
            rotulo="Data de nascimento"
            obrigatorio
            erro={erros.dataNascimento}
            campo="dataNascimento"
          >
            <Input
              type="date"
              /* O seletor nativo já impede escolher amanhã; a validação cobre o
                 que for digitado à mão. */
              max={new Date().toISOString().slice(0, 10)}
              value={form.dataNascimento}
              onChange={(evento) => definir('dataNascimento', evento.target.value)}
            />
          </Field>

          <Field
            rotulo="Sexo"
            obrigatorio
            erro={erros.sexo}
            campo="sexo"
            grupo
            className="sm:tw-col-span-2"
          >
            <RadioCards
              nome="sexo"
              opcoes={OPCOES_SEXO}
              valor={form.sexo}
              aoMudar={(valor) => definir('sexo', valor)}
            />
          </Field>
        </div>
      </FormSection>

      {/* ── 3. Contatos ─────────────────────────────────────────────────── */}
      <FormSection titulo="Contatos" icone="contato">
        <div className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2">
          <Field
            rotulo="E-mail"
            obrigatorio
            erro={erros.email}
            campo="email"
            className="sm:tw-col-span-2"
          >
            <Input
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="exemplo@email.com.br"
              value={form.email}
              onChange={(evento) => definir('email', evento.target.value)}
            />
          </Field>

          {/* DDD e número num campo só (spec 03 §3.3). O estado continua
              guardando os dois separados — ver `separarDddNumero` no submit. */}
          <Field rotulo="Celular" obrigatorio erro={erros.celularUnificado} campo="celularUnificado">
            <Input
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              placeholder="(11) 99999-9999"
              maxLength={16}
              value={form.celularUnificado}
              onChange={(evento) => definir('celularUnificado', formatTelefone(evento.target.value))}
            />
          </Field>

          <Field
            rotulo="Telefone fixo"
            erro={erros.telefoneFixoUnificado}
            campo="telefoneFixoUnificado"
            dica="Opcional"
          >
            <Input
              type="tel"
              inputMode="numeric"
              autoComplete="tel-national"
              placeholder="(11) 4002-8922"
              maxLength={16}
              value={form.telefoneFixoUnificado}
              onChange={(evento) =>
                definir('telefoneFixoUnificado', formatTelefone(evento.target.value))
              }
            />
          </Field>
        </div>
      </FormSection>

      {/* ── 4. Regional ─────────────────────────────────────────────────── */}
      <FormSection titulo="Regional" icone="local">
        <Field
          rotulo="Regional"
          obrigatorio
          erro={erros.regionalId}
          campo="regionalId"
          grupo
          dica="Define cota, produtos e rateio do veículo nas etapas seguintes."
        >
          {/* at-62: era um `BotaoRegional` local, idêntico ao `BotaoSelecao` do
              `FormularioVeiculo` — os dois viraram o primitivo do kit. */}
          <BotaoSelecao
            valor={form.regionalNome}
            vazio="Nenhuma regional selecionada"
            temErro={!!erros.regionalId}
            aoAbrir={() => setModalRegionaisAberto(true)}
          />
        </Field>
      </FormSection>

      {/* ── 5. Endereço ─────────────────────────────────────────────────── */}
      <FormSection titulo="Endereço" icone="endereco">
        <div className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2">
          <Field
            rotulo="CEP"
            obrigatorio
            erro={erros.cep}
            campo="cep"
            dica="Preenche o endereço automaticamente."
          >
            <div className="tw-relative">
              <Input
                type="tel"
                inputMode="numeric"
                autoComplete="postal-code"
                placeholder="00000-000"
                maxLength={9}
                value={form.cep}
                onChange={(evento) => definir('cep', formatCep(evento.target.value))}
                onBlur={() => void consultarCep()}
              />
              {buscandoCep && (
                <span className="tw-absolute tw-right-3 tw-top-1/2 -tw-translate-y-1/2">
                  <span
                    className="tw-block tw-h-4 tw-w-4 tw-animate-spin tw-rounded-full tw-border-2 tw-border-superficie-borda tw-border-t-brand"
                    aria-hidden="true"
                  />
                </span>
              )}
            </div>
          </Field>

          {/* D5: aceita "S/N" e "12A" — antes era `replace(/\D/g,'')`, e quem mora
              em casa sem número não tinha o que digitar. */}
          <Field rotulo="Número" obrigatorio erro={erros.numero} campo="numero">
            <Input
              type="text"
              autoComplete="off"
              placeholder="123, S/N, 12A"
              maxLength={10}
              value={form.numero}
              onChange={(evento) => definir('numero', evento.target.value)}
            />
          </Field>

          <Field
            rotulo="Rua / Logradouro"
            obrigatorio
            erro={erros.rua}
            campo="rua"
            className="sm:tw-col-span-2"
          >
            <Input
              type="text"
              autoComplete="street-address"
              placeholder="Av. Paulista"
              maxLength={100}
              value={form.rua}
              onChange={(evento) => definir('rua', evento.target.value)}
            />
          </Field>

          <Field rotulo="Complemento" campo="complemento" className="sm:tw-col-span-2">
            <Input
              type="text"
              autoComplete="off"
              placeholder="Apto 42, Bloco B"
              value={form.complemento}
              onChange={(evento) => definir('complemento', evento.target.value)}
            />
          </Field>

          <Field rotulo="Bairro" obrigatorio erro={erros.bairro} campo="bairro">
            <Input
              type="text"
              autoComplete="address-level3"
              placeholder="Centro"
              value={form.bairro}
              onChange={(evento) => definir('bairro', evento.target.value)}
            />
          </Field>

          <Field rotulo="Cidade" obrigatorio erro={erros.cidade} campo="cidade">
            <Input
              type="text"
              autoComplete="address-level2"
              placeholder="São Paulo"
              value={form.cidade}
              onChange={(evento) => definir('cidade', evento.target.value)}
            />
          </Field>

          <Field
            rotulo="Estado (UF)"
            obrigatorio
            erro={erros.estado}
            campo="estado"
            className="sm:tw-col-span-2"
          >
            <Select
              opcoes={UFS}
              autoComplete="address-level1"
              value={form.estado}
              onChange={(evento) => definir('estado', evento.target.value)}
            />
          </Field>
        </div>
      </FormSection>

      <FormActions>
        <Botao
          type="submit"
          tamanho="lg"
          carregando={salvando}
          iconeDireita={salvando ? undefined : 'chevron-direita'}
          larguraTotal
          className="sm:tw-w-auto sm:tw-min-w-[220px]"
        >
          {salvando ? 'Salvando...' : rotuloAcao}
        </Botao>
      </FormActions>

      <ModalRegionaisUsuario
        isOpen={modalRegionaisAberto}
        onClose={() => setModalRegionaisAberto(false)}
        onSelectRegional={(regional: { codigo_regional: string; nome: string }) => {
          setForm((antes) => ({
            ...antes,
            regionalId: regional.codigo_regional,
            regionalNome: regional.nome,
          }));
          setErros((antes) => ({ ...antes, regionalId: undefined }));
          setModalRegionaisAberto(false);
        }}
      />

      {comImportacaoSga && (
        <ModalImportacaoSga
          aberto={modalImportacaoAberto}
          tipo="ASSOCIADO"
          preview={previewSga}
          carregando={buscandoNoSga}
          importando={importandoDoSga}
          onConfirmar={() => void confirmarImportacao()}
          onCancelar={() => {
            if (importandoDoSga) return;
            setModalImportacaoAberto(false);
            setPreviewSga(null);
          }}
        />
      )}
    </form>
  );
};

/** {@link DadosAssociado} → estado interno (junta os telefones). */
function paraEstado(dados: Partial<DadosAssociado>): Partial<EstadoForm> {
  const { dddCelular, celular, dddTelefone, telefoneFixo, sexo, ...resto } = dados;

  return {
    ...resto,
    // Legado: as telas antigas guardavam "masculino"/"feminino" em minúscula e
    // a etapa 3 convertia. Um rascunho gravado antes da at-58 chega assim.
    sexo: sexo ? String(sexo).toUpperCase() : '',
    celularUnificado: juntarDddNumero(dddCelular, celular),
    telefoneFixoUnificado: juntarDddNumero(dddTelefone, telefoneFixo),
  };
}

export default FormularioAssociado;
